import {dig, digg} from "diggerize"
import {memo, shapeComponent, ShapeComponent, Text, useBreakpoint, useI18n, useStyles} from "shared/base"
import {Pressable, StyleSheet, View} from "react-native"
import React, {useMemo} from "react"
import {School, User} from "models"
import ActiveStep from "./active-step"
import AppHistory from "shared/history"
import Button from "components/chalky-layout/button"
import Card from "components/chalky-layout/card"
import ChalkyCheckbox from "components/chalky-layout/checkbox"
import ChalkyInput from "components/chalky-layout/input"
import ChalkyLayout from "components/chalky-layout"
import {ChalkyLayoutSelectCountries} from "components/countries/select"
import {ChalkyLayoutSelectCountryStates} from "components/country-states/select"
import {ChalkyLayoutSelectSchools} from "components/schools/select"
import {countryOptions} from "components/utils/countries-select"
import Devise from "@kaspernj/api-maker/build/devise"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import PhoneNumber from "components/chalky-layout/phone-number"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import StripedPressable from "components/chalky-layout/striped-pressable"
import updateSession from "components/users/update-session"
import useCurrentCountry from "components/utils/use-current-country"
import useQueryParams from "on-location-changed/build/use-query-params"

const styles = StyleSheet.create({
  mb15: {
    marginBottom: 15
  },

  buttonContainer: {
    flexDirection: "row"
  },
  buttonContainerSmDown: {
    marginTop: 16,
    marginBottom: 32
  },
  buttonContainerMdUp: {
    marginTop: 48,
    justifyContent: "end"
  },

  header: {
    color: "#2c2c96",
    fontSize: 24,
    fontWeight: 700
  },

  rootContainer: {},
  rootContainerMdUp: {
    flexDirection: "row",
    width: "100%"
  },

  roleCardsContainer: {},
  roleCardsContainerXlUp: {
    flexDirection: "row",
    alignItems: "center"
  },

  rolesContainer: {
    flex: 1
  },
  rolesContainerMdUp: {
    flex: 1
  },

  continueButtonContainer: {},
  continueButtonContainerSmDown: {
    flex: 1,
    marginLeft: 10
  },

  yourRoleDescription: {
    marginBottom: 30,
    color: "#2c2c96",
    fontSize: 16
  },

  yourRoleColumn: {
    paddingTop: 37
  },
  yourRoleColumnMdUp: {
    marginTop: "auto",
    marginBottom: "auto",
    width: 300,
    paddingRight: 30
  },
  yourRoleColumnLgUp: {
    width: 500,
    paddingRight: 80
  },

  yourRoleWithDescription: {},
  yourRoleWithDescriptionMdUp: {
    marginTop: -12,
    marginLeft: 30
  },
  yourRoleWithDescriptionLgUp: {
    marginLeft: 52
  },

  yourRole: {
    marginBottom: -25,
    color: "#2c2c96",
    fontFamily: "DreamChaser",
    fontSize: 64
  },
  yourRoleSmDown: {
    textAlign: "center"
  },
  yourRoleMdUp: {
    fontFamily: "DreamChaser",
    fontSize: 64
  }
})

export default memo(shapeComponent(class RoutesUserCreateUserStep2 extends ShapeComponent {
  selectedSchool = null

  setup() {
    setLayout("front")

    const {t} = useI18n({namespace: "js.routes.user.create_user.step2"})
    const currentCountry = useCurrentCountry()
    const phoneCountry = useMemo(() => {
      if (currentCountry) {
        const phoneCountry = countryOptions.find((country) => digg(country, "code") == currentCountry.countryCode())

        if (!phoneCountry) {
          throw new Error(
            `No phone country could be found for ${currentCountry.countryCode()} in ${countryOptions.map((country) => country.code).join(", ")}`
          )
        }

        return phoneCountry
      }
    }, [])

    this.setInstance(useBreakpoint())
    this.setInstance({
      queryParams: useQueryParams(),
      t
    })
    this.useStates({
      acceptNewsletter: false,
      acceptTerms: false,
      country: null,
      createNewSchool: false,
      form: undefined,
      readMore: false,
      phoneCountry,
      phoneNumber: ""
    })

    this.schoolsQuery = useMemo(() => {
      const query = School.ransack()

      if (this.s.country) {
        query.ransack({country_id_eq: this.s.country.id()})
      }

      return query
    }, [this.s.country?.countryCode()])
  }

  render() { // eslint-disable-line complexity
    const {mdUp, schoolsQuery, smDown, t, xlUp} = this.tt
    const {country, createNewSchool, phoneCountry, readMore} = this.s
    const buttonContainerStyle = useStyles(styles, "buttonContainer")
    const continueButtonContainerStyle = useStyles(styles, "continueButtonContainer")
    const rootContainerStyle = useStyles(styles, "rootContainer")
    const yourRoleColumnStyle = useStyles(styles, "yourRoleColumn")
    const rolesContainerStyle = useStyles(styles, "rolesContainer")
    const yourRoleStyle = useStyles(styles, "yourRole")
    const yourFocusDescriptionStyle = useStyles(styles, "yourRoleDescription")
    const yourRoleWithDescriptionStyle = useStyles(styles, "yourRoleWithDescription")

    const inactiveSteps = () => (
      <View>
        <ActiveStep description={t(".youre_ready")} label={t(".step_3")} />
      </View>
    )

    return (
      <ChalkyLayout dataSet={this.layoutDataSet ||= {route: "user/create-user/step2"}} layout="front" maxWidth={1260} requireUser={false}>
        <View dataSet={this.rootContainerDataSet ||= {class: "root-container"}} style={rootContainerStyle}>
          <View dataSet={this.yourRoleColumnDataSet ||= {class: "your-role-column"}} style={yourRoleColumnStyle}>
            <ActiveStep description={t(".your_role")} label={t(".step_1")} />
            <ActiveStep active label={t(".step_2")} />
            <View style={yourRoleWithDescriptionStyle}>
              <Text style={yourRoleStyle}>
                {t(".your_preferences")}
              </Text>
              {!mdUp &&
                <View style={styles.mb15}>
                  <StripedPressable onPress={this.tt.onReadMorePress}>
                    <Text>
                      {readMore ? t(".read_less") : t(".read_more")}
                    </Text>
                  </StripedPressable>
                </View>
              }
              {(mdUp || readMore) &&
                <Text style={yourFocusDescriptionStyle}>
                  {t(".your_preferences_description")}
                </Text>
              }
            </View>
            {mdUp && inactiveSteps()}
          </View>
          <View dataSet={this.rolesContainerDataSet ||= {class: "roles-container"}} style={rolesContainerStyle}>
            <Card>
              <Form onSubmit={this.tt.onSubmit} setForm={this.setStates.form}>
                <View style={this.formContainerStyle ||= {flexDirection: "row", flexWrap: "wrap"}}>
                  <View
                    style={this.cache("firstNameContainerStyle", {
                      width: xlUp ? "50%" : "100%",
                      paddingRight: xlUp ? 8 : undefined,
                      marginBottom: 20
                    }, [xlUp])}
                  >
                    <ChalkyInput
                      dataSet={this.firstNameInputDataSet ||= {class: "first-name-input"}}
                      label={t(".first_name")}
                      name="user[contact_attributes][first_name]"
                      placeholder={t(".woofie")}
                    />
                  </View>
                  <View
                    style={this.cache("lastNameContainerStyle", {
                      width: xlUp ? "50%" : "100%",
                      paddingLeft: xlUp ? 8 : undefined
                    }, [xlUp])}
                  >
                    <ChalkyInput
                      dataSet={this.lastNameInputDataSet ||= {class: "last-name-input"}}
                      label={t(".last_name")}
                      name="user[contact_attributes][last_name]"
                      placeholder={t(".woofington")}
                    />
                  </View>
                  <View
                    style={this.cache("countryContainerStyle", {
                      width: xlUp && country?.countryCode() == "US" ? "50%" : "100%",
                      paddingRight: xlUp && country?.countryCode() == "US" ? 8 : undefined,
                      marginBottom: 20
                    }, [xlUp, country?.countryCode() == "US"])}
                  >
                    <ChalkyLayoutSelectCountries
                      id="country"
                      label={t(".country_of_residence")}
                      name="user[contact_attributes][country_id]"
                      onChange={this.tt.onCountryChange}
                    />
                  </View>
                  {country?.countryCode() == "US" &&
                    <View
                      style={this.cache("countryStateContainerStyle", {
                        width: xlUp ? "50%" : "100%",
                        paddingLeft: xlUp ? 8 : undefined,
                        marginBottom: 20
                      }, [xlUp])}
                    >
                      <ChalkyLayoutSelectCountryStates id="country_state" label={t(".state")} name="user[contact_attributes][country_state_id]" />
                    </View>
                  }
                  <View style={this.schoolsContainerStyle ||= {width: "100%", marginBottom: 20}}>
                    {createNewSchool &&
                      <ChalkyInput
                        dataSet={this.newSchoolNameInputDataSet ||= {class: "new-school-name-input"}}
                        label={t(".new_school_name")}
                        name="new_school[name]"
                        placeholder={t(".create_your_school_by_typing_in_your_school_name_here")}
                      />
                    }
                    {!createNewSchool &&
                      <ChalkyLayoutSelectSchools
                        id="school"
                        inputContainerProps={this.schoolsSelectContainerProps ||= {rightOfLabel: this.rightOfSchoolsSelectLabel()}}
                        label={t(".search_existing_school")}
                        noOptionsText={this.tt.noSchoolOptionsText}
                        onChange={this.tt.onSchoolChange}
                        placeholder={t(".search_by_start_typing")}
                        query={schoolsQuery}
                      />
                    }
                  </View>
                  <View style={this.phoneNumberContainerStyle ||= {width: "100%", marginBottom: 20}}>
                    <PhoneNumber
                      country={phoneCountry}
                      label={t(".phone_number")}
                      number={this.s.phoneNumber}
                      onChangeCountry={this.setStates.phoneCountry}
                      onChangeNumber={this.setStates.phoneNumber}
                      placeholder={t(".placeholder_phone_number")}
                      value={this.s.phoneNumber}
                    />
                  </View>
                  <View
                    style={this.cache("emailContainerStyle", {
                      width: xlUp ? "50%" : "100%",
                      paddingRight: xlUp ? 8 : undefined,
                      marginBottom: 20
                    }, [xlUp])}
                  >
                    <ChalkyInput
                      dataSet={this.emailInputDataSet ||= {class: "email-input"}}
                      label={t(".email")}
                      name="user[email]"
                      placeholder={t(".email_at_woof_dot_com")}
                    />
                  </View>
                  <View
                    style={this.cache("repeatEmailContainerStyle", {
                      width: xlUp ? "50%" : "100%",
                      paddingLeft: xlUp ? 8 : undefined
                    }, [xlUp])}
                  >
                    <ChalkyInput
                      dataSet={this.emailConfirmationInputDataSet ||= {class: "email-confirmation-input"}}
                      label={t(".repeat_email")}
                      name="user[email_confirmation]"
                      placeholder={t(".email_at_woof_dot_com")}
                    />
                  </View>
                  <View
                    style={this.cache("passwordContainer", {
                      width: xlUp ? "50%" : "100%",
                      paddingRight: xlUp ? 8 : undefined,
                      marginBottom: 20
                    }, [xlUp])}
                  >
                    <ChalkyInput
                      dataSet={this.passwordInputDataSet ||= {class: "password-input"}}
                      label={t(".password")}
                      name="user[password]"
                      placeholder={t(".lots_of_stars")}
                      secureTextEntry
                    />
                  </View>
                  <View
                    style={this.cache("repeatPasswordStyle", {
                      width: xlUp ? "50%" : "100%",
                      paddingLeft: xlUp ? 8 : undefined
                    }, [xlUp])}
                  >
                    <ChalkyInput
                      dataSet={this.passwordConfirmationInputDataSet ||= {class: "password-confirmation-input"}}
                      label={t(".repeat_password")}
                      name="user[password_confirmation]"
                      placeholder={t(".lots_of_stars")}
                      secureTextEntry
                    />
                  </View>
                  <View style={this.acceptTermsContainerStyle ||= {flexShrink: 1, marginTop: 20, marginBottom: 20}}>
                    <ChalkyCheckbox
                      checked={this.s.acceptTerms}
                      dataSet={this.acceptTermsDataSet ||= {class: "accept-terms"}}
                      label={t(".accept_terms_label")}
                      name="user[notification_setting_attributes][accept_terms]"
                      onChangeChecked={this.setStates.acceptTerms}
                    />
                  </View>
                  <View style={this.acceptNewsletterContainerStyle ||= {flexShrink: 1}}>
                    <ChalkyCheckbox
                      checked={this.s.acceptNewsletter}
                      dataSet={this.acceptNewsletterDataSet ||= {class: "accept-marketing"}}
                      label={t(".newsletter_label")}
                      name="user[notification_setting_attributes][accept_marketing]"
                      onChangeChecked={this.setStates.acceptNewsletter}
                    />
                  </View>
                </View>
              </Form>
            </Card>
            <View style={buttonContainerStyle}>
              <View style={continueButtonContainerStyle}>
                <Button
                  dataSet={this.continueButtonDataSet ||= {class: "continue-button"}}
                  onPress={this.tt.onContinuePress}
                  primary
                  title={t(".continue")}
                />
              </View>
            </View>
          </View>
          {smDown && inactiveSteps()}
        </View>
      </ChalkyLayout>
    )
  }

  noSchoolOptionsText = () => this.t(".no_match_you_can_create_your_school_by_clicking_the_create_new_school_above_to_the_right")

  onCountryChange = ({options}) => {
    const country = dig(options, 0, "country")
    const newState = {country}

    if (country) {
      newState.phoneCountry = countryOptions.find((countryOption) => countryOption.code == country.countryCode())
    }

    this.setState(newState)
  }

  onSchoolChange = ({options}) => this.tt.selectedSchool = dig(options, 0, "school")
  onSubmit = () => this.tt.onContinuePress()

  onContinuePress = async () => {
    const {queryParams, selectedSchool, t} = this.tt
    const {phoneCountry, phoneNumber} = this.s
    const formData = this.s.form.asObject()

    if (phoneNumber == "") {
      delete formData.user.contact_attributes.contact_phone_numbers_attributes
    } else {
      const dialCode = dig(phoneCountry, "dialCode")

      formData.user.contact_attributes.contact_phone_numbers_attributes = [{
        phone_number: `${dialCode}${phoneNumber}`,
        primary: true
      }]
    }

    const contactInfosAttributes = []

    if (selectedSchool) {
      formData.school_id = selectedSchool.id()
    }

    if (queryParams.academic_performance_value) {
      contactInfosAttributes.push({
        info_type: "integer",
        name: "academic_performance_value",
        value: parseInt(queryParams.academic_performance_value)
      })
    }

    if (queryParams.class_room_community_value) {
      contactInfosAttributes.push({
        info_type: "integer",
        name: "class_room_community_value",
        value: parseInt(queryParams.class_room_community_value)
      })
    }

    if (queryParams.socio_emotional_life_skills_value) {
      contactInfosAttributes.push({
        info_type: "integer",
        name: "socio_emotional_life_skills_value",
        value: parseInt(queryParams.socio_emotional_life_skills_value)
      })
    }

    if (queryParams.focus) {
      contactInfosAttributes.push({
        info_type: "string",
        name: "focus",
        value: queryParams.focus
      })
    }

    if (queryParams.role) {
      contactInfosAttributes.push({
        info_type: "string",
        name: "role",
        value: queryParams.role
      })
    }

    if (contactInfosAttributes.length > 0) {
      formData.user.contact_attributes.contact_infos_attributes = contactInfosAttributes
    }

    try {
      const result = await User.createUser(formData)
      const user = digg(result, "user")

      await updateSession(user.id())
      FlashMessage.success(t(".your_user_have_been_created"))

      if (!Devise.currentUser()) {
        throw new Error("User wasn't set")
      }

      AppHistory.push(Routes.userCreateUserStep3Path(queryParams))
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  onReadMorePress = () => this.setState({readMore: !this.s.readMore})

  rightOfSchoolsSelectLabel() {
    const {t} = this.tt

    return (
      <Pressable onPress={this.tt.onCreateNewSchoolPress} testID="create-new-school-pressable">
        <Text style={this.createNewSchoolTextStyle ||= {color: "#2c2c90"}}>
          {t(".create_new_school")}
        </Text>
      </Pressable>
    )
  }

  onCreateNewSchoolPress = () => this.setState({createNewSchool: true})
}))
